/**
 * Populate product data for reservation
 * @param {*} product - The product object
 * @returns {Object|null} - The populated product data or null if product is not provided
 */


import { v4 as uuidv4 } from "uuid";
import { buildVehicleCategoryLabel, buildVehicleDisplayName, getQuoteRouterReference, getQuoteType, getTitleByGender, parserContactNumber } from "./format.service";
import { getGenderByTitle } from "../../../core/services/passenger.service";


export function populateProduct(product) {
    if (!product) return null;
    return {
        _id: product._id,
        name: product.name,
        code: product.code,
        type: product.type,
        passengers: product.passengers,
        bags: product.bags,
        images: product.images,
        currency: product.currency,
        pricing: product.pricing,
        slug: product.slug,
        destination: product.destination,
        shortdescription: product.short_description,
        durationUnit: product.duration.unit,
        durationValue: product.duration.value,
        pricing_method: product.pricing_method,
        area: product.area,
        transfer: product.transfer,
        service: product.service,
        supplier: product.supplier,
        branch: product.branch,
        duration: product.duration,
        show_options: product.show_options
    };
}

/**
 * Populate agent data for reservation
 * @param {*} agent - The agent object
 * @returns {Object|null} - The populated agent data or null if agent is not provided
 */
export function populateAgent(agent) {
    if (!agent) return null;
    return {
        _id: agent._id,
        first_name: agent.first_name,
        last_name: agent.last_name,
        gender: agent.gender,
        email: agent.email,
        phone: agent.phone,
        other_contact_platform: agent.other_contact_platform,
        contact_info: agent.contact_info,
        type: agent.type,
        owner: agent.association._id,
        company_name: agent.association ? agent.association.company_name : "",
        mark_up: agent.association ? agent.association.mark_up : 0.68,
        type_markup: agent.association
            ? agent.association.type_markup
            : "markup",
        commission: agent.commission,
        commercialTerms: agent.type_charge,
        currency: agent.association
            ? agent.association.currency
            : { code: "USD", symbol: "$", label: "US Dollar" },
        credit: agent.association
            ? agent.association.credit
            : { status: false, limit: 0, available: 0 }
    };
}

/**
 * Populate customer data for reservation
 * @param {*} customer - The customer object
 * @returns {Object|null} - The populated customer data or null if customer is not provided
 */
export function populateCustomer(customer) {
    if (!customer) return null;
    return {
        _id: customer._id,
        first_name: customer.first_name,
        last_name: customer.last_name,
        gender: customer.gender,
        email: customer.email,
        phone: customer.phone,
        other_contact_platform: customer.other_contact_platform,
        contact_info: customer.contact_info,
        type: customer.type,
        company_name: customer.company_name,
        mark_up: customer.mark_up,
        type_markup: customer.type_markup,
        commission: customer.commission,
        commercialTerms: customer.type_charge,
        currency: customer.currency
            ? customer.currency
            : { code: "USD", symbol: "$", label: "US Dollar" },
        credit: customer.currency
            ? customer.credit
            : { status: false, limit: 0, available: 0 }
    };
}

/**
 * Populate driver data for reservation
 * @param {*} driver - The driver object
 * @returns {Object} - The populated driver data
 */
export function populateDriver(driver) {
    if (!driver) return null;
    return {
        person: {
            first_name: driver.person.first_name,
            last_name: driver.person.last_name,
            full_name: driver.person.full_name,
            gender: driver.person.gender,
            numbre_phone: driver.person.numbre_phone
        },
        profile: {
            image: driver.profile.image
        },
        relationship: {
            branch: driver.relationship.branch,
            auto_assign: driver.relationship.auto_assign
        },
        account: {
            email: driver.account.email
        }
    };
}

/**
 * Populate service data for reservation
 * @param {Object} reservation - The reservation object
 * @param {Object} vehicle - The vehicle object
 * @returns {Object|null} - The populated service data or null if service is not provided
 */
export function populateService(reservation, vehicle) {
    let { service, pricing, supplier } = reservation;
    if (!service) return null;
    return {
        _id: service._id,
        vehicle: {
            _id: vehicle._id,
            seats: vehicle.parameters.n_seats,
            suitcases: vehicle.parameters.n_suitcases,
            type_service: vehicle.parameters.type_category,
            category: vehicle.parameters.category,
            make: vehicle.parameters.make,
            make_label: vehicle.parameters.name,
            image: vehicle.information.image,
            tags: vehicle.parameters.tags || [],
            isComposite: vehicle.parameters.isComposite
        },
        hours_advance: service.advancedReservation,
        preference: service.preference,
        currency: {
            code: service.currency_code,
            symbol: service.currency_symbol,
            label: service.currency_label
        },
        branchId: service.branch,
        supplierId: supplier ? supplier._id : null,
        base_price: pricing.base_price.last_amount,
        sale_price: pricing.sale_price.last_amount,
        basePriceComposition: pricing.base_price.composition,
        surcharges: reservation.surcharges,
        unit: supplier ? supplier.settings.measurementUnit : "km",
        distance_include: service.hours_disposition
            ? service.hours_disposition.km_include
            : 0,
        min_hours: service.hours_disposition
            ? service.hours_disposition.min_hours
            : 0,
        cancellationPolicy: service.cancellationPolicy
            ? service.cancellationPolicy.descriptions
            : [],
        keyDetails: service.keyDetails,
        termsConditions: service.termsConditions,
    };
}

/** * Maps a booking object to a quote structure.
 * The reservation `event` is left out: quotes do not store events and the quote
 * create endpoint rejects unknown fields.
 * @param {Object} booking - The booking object to map.
 * @returns {Object} The mapped quote object.
 */
export function mapQuoteFromBooking(booking, confirmationReason = null) {
    if (!booking) return null;
    // eslint-disable-next-line no-unused-vars
    const { event, ...quote } = booking;
    return {
        confirmationReason: confirmationReason,
        ...quote
    };
}

/** * Builds a booking summary object from reservation, service, and passenger data.
 * @param {Object} reservation - The reservation object containing booking details.
 * @param {Object} service - The service object associated with the reservation.
 * @param {Object} passenger - The passenger object associated with the reservation.
 * @returns {Object} The booking summary object.
 */
export function buildBookingSummary(reservation, service, passenger) {
    if (!reservation || !service) return null;
    return {
        from: reservation.from,
        to: reservation.to,
        pickupDate: reservation.pickup_date,
        pickupTime: reservation.pickup_time,
        passengersCount: reservation.passengersCount,
        vehicleCategory: buildVehicleCategoryLabel(service.vehicle),
        vehicleName: buildVehicleDisplayName(service.vehicle),
        passenger: passenger ? {
            firstName: passenger.firstName,
            lastName: passenger.lastName,
            fullName: passenger.fullName,
            email: passenger.email,
            phone: passenger.phone,
            gender: passenger.gender,
            type: passenger.type
        } : null,
        currency: service.currency ? service.currency : null,
        totalAmount: reservation.pricing ? reservation.pricing.totals.gross_price : 0,
        specialRequirements: reservation.special_requirements,
    }
};

/**
 * Maps raw imported CSV rows into the internal passenger object shape.
 * @param {Array} passengers - The raw imported passenger rows.
 * @returns {Array} - The mapped passenger objects.
 */
export function parsePassengers(passengers = []) {
    return passengers.map((passenger, index) => {
        return {
            _id: null,
            reference: uuidv4(),
            firstName: passenger.firstName,
            lastName: passenger.lastName,
            fullName: passenger.fullName,
            email: passenger.emailAddress,
            phone: parserContactNumber(passenger.contactNumber),
            gender: getGenderByTitle(passenger.title),
            type: passenger.type ? passenger.type : "ADULT",
            primary: false,
            notify: passenger.sendNotification
                ? passenger.sendNotification.toUpperCase() === "YES"
                : false,
            flightNumber: passenger.flightNumber
        };
    });
}

/**
 * Builds the pickup sign text/format from the current primary passenger's name.
 * @param {Array} passengers - The reservation's passenger list.
 * @returns {Object} - The { format, text, link } pickup sign object.
 */
export function getPickupSignByPrimaryPassenger(passengers = []) {
    let passenger = passengers.find(passenger => passenger.primary === true);
    return {
        format: "CUSTOM",
        text: passenger
            ? `${getTitleByGender(passenger.gender)} ${
                passenger.firstName
            } ${passenger.lastName}`
            : "",
        link: ""
    };
}

/**
 * Finds a customer object in a list by id.
 * @param {Array} customers - The list of customers to search.
 * @param {string} customerId - The id of the customer to find.
 * @returns {Object|undefined} - The matching customer.
 */
export function getCustomerById(customers = [], customerId) {
    return customers.find(customer => customer._id === customerId);
}

/**
 * Normalizes an agent reference (object or id string) to just the id.
 * @param {Object|string|null} agent - The agent reference.
 * @returns {string|null} - The agent id.
 */
export function getAgentId(agent) {
    if (agent) {
        return typeof agent === "object" ? agent._id : agent;
    } else {
        return null;
    }
}

/**
 * Builds an agent record combining agent personal data with the parent customer's commercial data
 * (markup, currency, credit) for a reservation being duplicated.
 * @param {Object} agent - The agent's personal data.
 * @param {Object} customer - The parent customer's commercial data.
 * @returns {Object} - The combined agent record.
 */
export function populateReservationAgent(agent, customer) {
    return {
        _id: agent._id,
        first_name: agent.first_name,
        last_name: agent.last_name,
        gender: agent.gender,
        phone: agent.phone,
        type: agent.type,
        owner: agent.association,
        company_name: customer.company_name,
        mark_up: customer.mark_up,
        type_markup: customer.type_markup,
        currency: customer.currency,
        credit: customer.credit
    };
}